import React from 'react'
import { useNavigate } from 'react-router-dom'
import { MapPin, Calendar, DollarSign, Clock, Briefcase, ArrowRight } from 'lucide-react'
import DataResultsContainer from './DataResultsContainer.tsx'
import styles from './ConversationalMode.module.scss'

interface WorkOffersListProps {
  offers: any[]
  total?: number
}

const formatPostedTime = (createdAt?: string) => {
  if (!createdAt) return "Reciente"
  const createdDate = new Date(createdAt)
  if (Number.isNaN(createdDate.getTime())) return "Reciente"
  const diff = Date.now() - createdDate.getTime()
  if (diff < 60 * 1000) return "Hace instantes"
  if (diff < 60 * 60 * 1000) {
    const minutes = Math.floor(diff / (60 * 1000))
    return `Hace ${minutes} min`
  }
  if (diff < 24 * 60 * 60 * 1000) {
    const hours = Math.floor(diff / (60 * 60 * 1000))
    return `Hace ${hours} h`
  }
  const days = Math.floor(diff / (24 * 60 * 60 * 1000))
  return `Hace ${days} día${days === 1 ? "" : "s"}`
}

const formatSalary = (salary?: string, salaryType?: string) => {
  if (!salary || salary === "0.00" || Number.parseFloat(salary) === 0) {
    return "A convenir"
  }
  const base = Math.floor(Number.parseFloat(salary)).toLocaleString("es-AR")
  switch (salaryType) {
    case "Hour":
      return `$${base} / hora`
    case "Day":
      return `$${base} / día`
    case "Week":
      return `$${base} / semana`
    case "Month":
      return `$${base} / mes`
    case "Job":
      return `$${base} / proyecto`
    default:
      return `$${base}`
  }
}

const formatDateLabel = (date?: string) => {
  if (!date) return "Sin dato"
  const parsed = new Date(date)
  if (Number.isNaN(parsed.getTime())) return "Sin dato"
  return parsed.toLocaleDateString("es-AR", { day: "2-digit", month: "short" })
}

const WorkOffersList: React.FC<WorkOffersListProps> = ({ offers, total }) => {
  const navigate = useNavigate()
  const displayTotal = total || offers.length

  return (
    <DataResultsContainer
      icon={<Briefcase size={18} />}
      title={`${offers.length} ${offers.length === 1 ? 'oferta disponible' : 'ofertas disponibles'}`}
      subtitle={displayTotal > 3 ? `de ${displayTotal} totales` : undefined}
      color="blue"
      total={displayTotal}
      maxDisplay={3}
      viewAllLink="/bolsa-trabajo"
      viewAllText={`Ver las ${displayTotal} ofertas en Bolsa de Trabajo`}
      isEmpty={offers.length === 0}
    >
      {offers.slice(0, 3).map((offer) => {
        const companyLabel =
          offer?.user?.type === "COMPANY"
            ? offer.user?.razon_social ?? "Empresa"
            : `${offer.user?.name ?? ""} ${offer.user?.last_name ?? ""}`.trim() || "Publicador"

        const skillLabels = (offer.skill ?? [])
          .map((item: any) => item?.skill?.name)
          .filter(Boolean)
          .slice(0, 2) as string[]

        const primaryZone =
          offer.workZones?.[0]?.jurisdiccion?.name ??
          offer.workZones?.[0]?.city ??
          "Ubicación a confirmar"

        const postedLabel = formatPostedTime(offer.createdAt)
        const salaryLabel = formatSalary(offer.salary, offer.salaryType)
        const startLabel = offer.immediateStart
          ? "Inicio inmediato"
          : formatDateLabel(offer.startDate)
        const activeUntilLabel = formatDateLabel(offer.activeUntil)

        return (
          <div key={offer.id} className={`${styles.resultCard} ${styles.resultCardBlue}`}>
            <div className={styles.resultCardHeader}>
              <span className={styles.resultCardTitle}>{companyLabel}</span>
              <span className={`${styles.resultCardBadge} ${styles.resultCardBadgeBlue}`}>{postedLabel}</span>
            </div>

            {skillLabels.length > 0 && (
              <div className={styles.resultCardChips}>
                {skillLabels.map((label) => (
                  <span key={label} className={styles.resultCardChip}>{label}</span>
                ))}
              </div>
            )}

            <div className={styles.resultCardMeta}>
              <span><MapPin size={12} /> {primaryZone}</span>
              <span><Calendar size={12} /> {startLabel}</span>
              <span><DollarSign size={12} /> {salaryLabel}</span>
              <span><Clock size={12} /> Hasta {activeUntilLabel}</span>
            </div>

            <button
              type="button"
              className={`${styles.resultCardCta} ${styles.resultCardCtaBlue}`}
              onClick={() => navigate(`/bolsa-trabajo/${offer.id}`)}
            >
              Ver oferta <ArrowRight size={14} />
            </button>
          </div>
        )
      })}
    </DataResultsContainer>
  )
}

export default WorkOffersList
